/* eslint-disable max-len */
/* eslint-disable react/prop-types */

import React, { useContext, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { useConfigService, ExperienceContext } from '@thd-nucleus/experience-context';
import { Carousel } from '@one-thd/sui-carousel';
import proptypes from 'prop-types';
import { getSponsoredHeroBanner } from '@thd-olt-component-react/sponsored-content';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { ComponentSelector } from '@thd-olt-component-react/component-selector';
import { BentoBay, BentoBayRenderer } from './BentoBay';
import { COMPONENT_SELECTOR } from '../constants';
import './stencil-overrides.scss';

const {
  shape, bool, arrayOf, number
} = proptypes;

let sponsoredBannerPromise = null;

export function resetSponsoredBannerRequested() {
  sponsoredBannerPromise = null;
}

export function BentoHeroCarousel({
  carouselData,
  isAutoplay = false,
  position = 0,
  enableB2CRadius = false,
  variationIds = [],
  renderDefault = false,
  isSeoBot = false,
  impressionsAreReady = false
}) {
  let initialCarouselItems = [];
  const experienceContext = useContext(ExperienceContext);
  const channel = experienceContext.channel;
  const [sponsoredHero, setSponsoredHero] = useState(null);
  const [sponsoredHeroFetched, setSponsoredHeroFetched] = useState(false);
  const carouselRef = useRef(null);

  const swiperTimer = isAutoplay ? { autoplay: { delay: 8000, pauseOnMouseEnter: true } } : null;
  const thdCustomer = useThdCustomer();
  const revJetConfigRehub = useConfigService('revJetConfigMap');
  const callBannerApiForHero = useConfigService('callBannerApiForHero');
  let externalRevJetConfig = null;
  if (revJetConfigRehub) {
    try {
      externalRevJetConfig = JSON.parse(revJetConfigRehub);
    } catch (err) {
      // newrelic logging coming in future story
    }
  }

  // eslint-disable-next-line no-restricted-syntax
  for (let [index, child] of carouselData.entries()) {
    initialCarouselItems.push(<div key={`item_${index}`}>{child}</div>);
    // Only one carousel image needs to be rendered server-side
    if (typeof window === 'undefined') break;
  }

  if (sponsoredHero) {
    initialCarouselItems.splice(1, 0, sponsoredHero);
  }

  async function checkForSponsoredBanner() {
    if (!sponsoredBannerPromise) {
      sponsoredBannerPromise = getSponsoredHeroBanner({
        channel,
        classNames: 'sui-w-full sui-h-full',
        experienceContext,
        externalRevJetConfig,
        browserId: thdCustomer?.mcvisID || '',
        returnSponsoredHeroOnly: true,
        callBannerApiForHero
      });
    }
    const sponsoredHeroBanner = await sponsoredBannerPromise;
    setSponsoredHeroFetched(true);
    setSponsoredHero(sponsoredHeroBanner?.HeroBanner || null);
  }

  useEffect(() => {
    if (!sponsoredHeroFetched && thdCustomer?.mcvisID) {
      checkForSponsoredBanner();
    }
  }, [thdCustomer?.mcvisID, sponsoredHeroFetched]);

  useEffect(() => {
    const container = carouselRef.current;
    if (!container || typeof window.ResizeObserver === 'undefined') return undefined;
    let rafId;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    });
    observer.observe(container);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, []);
  const wrapperClasses = classnames('remove-top-stencil-margin sui-col-span-12 lg:sui-col-span-8 sui-order-1 lg:sui-aspect-[3.6/1] sui-w-full', { 'round-carousel-images': enableB2CRadius });

  return (
    <div
      ref={carouselRef}
      id="hero-carousel"
      className={wrapperClasses}
      data-component="BentoHeroCarousel"
    >
      <div id="hero-carousel-wrapper">
        <Carousel
          layout="full-bleed"
          containerId="hero-carousel-wrapper"
          type="peekless"
          autoplay={isAutoplay}
          disableShadow
          disableMargin
          SwiperProps={swiperTimer}
        >
          {initialCarouselItems.map((item, index) => {
            const isSponsoredHero = sponsoredHero && index === 1;

            if (isSponsoredHero) {
              return <React.Fragment key={`hero-item-${index}`}>{item}</React.Fragment>;
            }

            const childData = item.props.children;

            if (childData?.__typename === COMPONENT_SELECTOR) {
              return (
                <div key={`hero-image-${index}-${impressionsAreReady}`} className="sui-w-full sui-h-full sui-overflow-hidden">
                  <ComponentSelector
                    componentMap={{ HeroFlattenImage: BentoBayRenderer }}
                    componentId={childData.id}
                    customPropMap={{
                      HeroFlattenImage: {
                        position,
                        isSponsored: true,
                        enableB2CRadius,
                      }
                    }}
                    defaultComponent={childData.defaultComponent}
                    isInCarousel
                    isSeoBot={isSeoBot}
                    renderDefault={renderDefault}
                    targetingType={childData.targetingType}
                    usePlaceholder={false}
                    variations={childData.variations}
                    variationIds={variationIds}
                  />
                </div>
              );
            }

            return (
              <BentoBay
                key={`hero-image-${index}`}
                bentoBayData={childData || {}}
                position={position}
                enableB2CRadius={enableB2CRadius}
              />
            );
          })}
        </Carousel>
      </div>
    </div>
  );
}

BentoHeroCarousel.propTypes = {
  carouselData: arrayOf(shape()).isRequired,
  isAutoplay: bool,
  position: number,
  enableB2CRadius: bool,
  variationIds: arrayOf(shape()),
  renderDefault: bool,
  isSeoBot: bool,
  impressionsAreReady: bool
};
